import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';

import toKebabCase from 'utils/toKebabCase';

export const FEATURE_FLAGS = {
  DONATION_SIDEBAR_REDIRECT: 'newsletter-sidebar-donation-redirect',
  HOMEPAGE_ELECTION_REPORTING: 'homepage-election-reporting',
} as const;

export interface FeatureFlag {
  title: string;
  isEnabled: boolean;
}

export interface FeatureFlagsData {
  flags: FeatureFlag[];
}

interface FeatureFlagsContextValue {
  flags: FeatureFlag[];
  isFeatureEnabled: (title: string) => boolean;
}

interface FeatureFlagsProviderProps {
  children: ReactNode;
}

const defaultValue: FeatureFlagsContextValue = {
  flags: [],
  isFeatureEnabled: () => false,
};

const FeatureFlagsContext =
  createContext<FeatureFlagsContextValue>(defaultValue);

export const FeatureFlagsProvider = ({
  children,
}: FeatureFlagsProviderProps) => {
  const [flags, setFlags] = useState<FeatureFlag[]>([]);

  useEffect(() => {
    async function getData() {
      try {
        const response = await fetch('/api/featureFlags');
        const parsed: FeatureFlagsData = await response.json();
        setFlags(parsed.flags || []);
      } catch (err) {
        console.error('Error fetching feature flags:', err);
      }
    }
    getData();
  }, []);

  const isFeatureEnabled = (title: string) =>
    flags.find((flag) => toKebabCase(flag.title) === title)?.isEnabled ?? false;

  return (
    <FeatureFlagsContext.Provider value={{ flags, isFeatureEnabled }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export const useFeatureFlags = () => {
  return useContext(FeatureFlagsContext);
};

export default FeatureFlagsContext;
